import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, UtensilsCrossed } from "lucide-react";
import { api, formatApiError, fmtDate, fmtUSD } from "@/lib/api";
import { Label, Input, PrimaryButton, GhostButton } from "@/components/fields";

const STATUS_COLORS = {
  pending_confirmation: "bg-gold/15 text-gold",
  confirmed: "bg-teal/15 text-teal",
  declined: "bg-ink/10 text-ink/50",
};

export default function FoodAdminPanel() {
  const [menu, setMenu] = useState(null);
  const [requests, setRequests] = useState([]);
  const [saving, setSaving] = useState(false);
  const [quoteFor, setQuoteFor] = useState({});
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const [m, r] = await Promise.all([
        api.get("/food-services/menu"),
        api.get("/admin/food-requests"),
      ]);
      setMenu(m.data);
      setRequests(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setItem = (idx, patch) => {
    const items = menu.items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    setMenu({ ...menu, items });
  };

  const saveMenu = async () => {
    setSaving(true);
    try {
      await api.put("/admin/food-services/menu", {
        min_guests: Number(menu.min_guests),
        advance_hours: Number(menu.advance_hours),
        items: menu.items.map((i) => ({ ...i, price: Number(i.price) || 0 })),
      });
      toast.success("Menu saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (id, status) => {
    setBusy(id + status);
    try {
      const body = { status };
      const qp = quoteFor[id];
      if (status === "confirmed" && qp) body.quoted_price = Number(qp);
      await api.patch(`/admin/food-requests/${id}`, body);
      toast.success(`Request ${status}`);
      setRequests((cur) => cur.map((r) => (r.id === id ? { ...r, status, quoted_price: body.quoted_price ?? r.quoted_price } : r)));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  if (!menu) return <p className="text-sm text-ink/50 py-8">Loading…</p>;

  return (
    <div className="space-y-8" data-testid="food-admin-panel">
      <section className="bg-white rounded-3xl border border-ink/10 p-6 space-y-4">
        <h3 className="font-semibold text-ink flex items-center gap-2"><UtensilsCrossed size={17} className="text-terracotta" /> Menu & pricing</h3>
        <div className="grid grid-cols-2 gap-3 max-w-md">
          <div>
            <Label htmlFor="fa-min">Minimum guests (min charge)</Label>
            <Input id="fa-min" data-testid="food-admin-min-guests" type="number" min="1" max="20"
                   value={menu.min_guests} onChange={(e) => setMenu({ ...menu, min_guests: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="fa-adv">Advance notice (hours)</Label>
            <Input id="fa-adv" data-testid="food-admin-advance" type="number" min="0" max="720"
                   value={menu.advance_hours} onChange={(e) => setMenu({ ...menu, advance_hours: e.target.value })} />
          </div>
        </div>
        <div className="space-y-2">
          {menu.items.map((it, idx) => (
            <div key={it.id} data-testid={`food-admin-item-${it.id}`}
                 className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_110px_90px] gap-2 items-center border-b border-ink/5 pb-2">
              <Input value={it.label_en} data-testid={`food-admin-label-en-${it.id}`} onChange={(e) => setItem(idx, { label_en: e.target.value })} />
              <Input value={it.label_es} data-testid={`food-admin-label-es-${it.id}`} onChange={(e) => setItem(idx, { label_es: e.target.value })} />
              <Input type="number" min="0" step="1" value={it.price} data-testid={`food-admin-price-${it.id}`}
                     onChange={(e) => setItem(idx, { price: e.target.value })} placeholder="0 = quote" />
              <label className="flex items-center gap-2 text-xs text-ink/60">
                <input type="checkbox" checked={it.active} data-testid={`food-admin-active-${it.id}`}
                       onChange={(e) => setItem(idx, { active: e.target.checked })}
                       className="w-4 h-4 accent-terracotta" />
                Available
              </label>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-ink/45">Price 0 means “quote on request”. Descriptions are fixed; labels and prices are editable.</p>
        <PrimaryButton data-testid="food-admin-save" onClick={saveMenu} disabled={saving}>
          {saving ? "Saving…" : "Save menu"}
        </PrimaryButton>
      </section>

      <section className="space-y-3">
        <h3 className="font-semibold text-ink">Food service requests ({requests.length})</h3>
        {requests.length === 0 && <p className="text-sm text-ink/45">No requests yet.</p>}
        {requests.map((r) => (
          <div key={r.id} data-testid={`food-request-row-${r.id}`} className="bg-white rounded-2xl border border-ink/10 p-5">
            <div className="flex flex-wrap items-center gap-3">
              <p className="font-medium text-ink grow">{r.item_label_en}</p>
              <span className={`text-[11px] font-semibold rounded-full px-3 py-1 ${STATUS_COLORS[r.status] || ""}`}
                    data-testid={`food-request-status-${r.id}`}>{r.status.replace("_", " ")}</span>
            </div>
            <p className="text-xs text-ink/55 mt-1.5">
              {r.name} · {r.email} · {r.whatsapp || "no whatsapp"} · {fmtDate(r.start_date)}
              {r.end_date && r.end_date !== r.start_date ? ` → ${fmtDate(r.end_date)}` : ""} · {r.adults}+{r.children} guests
              {r.meal_times ? ` · ${r.meal_times}` : ""}
            </p>
            {r.reservation_ref && <p className="text-xs text-ink/45 mt-1">Ref: {r.reservation_ref}</p>}
            {r.allergies && <p className="text-xs text-terracotta mt-1">Allergies: {r.allergies}</p>}
            <p className="text-xs text-ink/60 mt-1">
              {r.estimated_total ? `Estimate $${fmtUSD(r.estimated_total)} (billed ${r.billed_guests} guests × ${r.days} day${r.days > 1 ? "s" : ""})` : "Quote required"}
              {r.quoted_price ? ` · Final quoted $${fmtUSD(r.quoted_price)}` : ""}
            </p>
            {r.status === "pending_confirmation" && (
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <Input data-testid={`food-quote-input-${r.id}`} type="number" min="0" step="1" placeholder="Final price (optional)"
                       value={quoteFor[r.id] || ""} className="!w-44 !py-1.5"
                       onChange={(e) => setQuoteFor({ ...quoteFor, [r.id]: e.target.value })} />
                <PrimaryButton data-testid={`food-confirm-btn-${r.id}`} onClick={() => setStatus(r.id, "confirmed")}
                               disabled={busy} className="!py-1.5 !text-xs">
                  <Check size={13} /> Confirm
                </PrimaryButton>
                <GhostButton data-testid={`food-decline-btn-${r.id}`} onClick={() => setStatus(r.id, "declined")}
                             disabled={busy} className="!py-1.5 !text-xs">
                  <X size={13} /> Decline
                </GhostButton>
              </div>
            )}
          </div>
        ))}
      </section>
    </div>
  );
}
